import { Dispatch, MouseEvent, ReactNode } from "react"

import { ListSearchInputProps } from "../../../../hooks/useListKeyboardNavigation"
import { ColumnReorderFn } from "../ColumnDnd/hooks/useColumnOrder"
import { TableProps } from "../Table"
import { StartColumnResizeCallback } from "../Table/types"
import { TableCellHeight, TableCellWidth } from "../TableCell"
import {
  DistributiveOmit,
  ItemNameInflections,
  TableFooterBarProps,
} from "../TableFooterBar/types"

export const ColumnType = {
  Data: "data",
  Group: "group",
} as const
export type ColumnType = (typeof ColumnType)[keyof typeof ColumnType]

export const RowReorderingMode = {
  Hidden: "hidden",
  Disabled: "disabled",
  Enabled: "enabled",
} as const
export type RowReorderingMode =
  (typeof RowReorderingMode)[keyof typeof RowReorderingMode]

interface BaseColumn {
  title: string
}

export interface DataColumn<T> extends BaseColumn {
  type: typeof ColumnType.Data
  renderTitle?: (title: string) => ReactNode
  renderCell: (rowData: T) => ReactNode
  width: TableCellWidth
  extractCellTestId?: (rowData: T) => string
}

export interface GroupColumn<T> extends BaseColumn {
  type: typeof ColumnType.Group
  accessory?: ReactNode
  renderTitle?: (title: string) => ReactNode
  columns: DataColumn<T>[]
  minVisibleColumns: number
}

export type Column<T> = DataColumn<T> | GroupColumn<T>

export type TableRowClickHandler<T> = (
  rowData: T,
  event: MouseEvent<HTMLTableRowElement>
) => void

export type RowSelectionKind = "single-select" | "multi-select"

import type { WebUrl } from "../../../../lib/webUrl"

export type UseTableProps<T> = {
  data: T[]
  columns: Column<T>[]
  cellHeight: TableCellHeight
  extractRowKey: (rowData: T) => string
  rowSelectionKind?: RowSelectionKind
  canSelectAllRows?: boolean
  enableRowHighlighting?: boolean
  isError?: boolean
  isLoading?: boolean
  onClickRow?: TableRowClickHandler<T>
  onLastRowInView?: () => void
  extractRowHref?: (rowData: T) => WebUrl | string
  itemNameInflections?: ItemNameInflections
  tableTestId?: string
  hiddenColumns?: Set<string>
  enableColumnReordering?: boolean
  lockedColumnsCount?: number
  onColumnReorder?: (columns: Column<T>[]) => void
  toggleColumnHideShow?: (columnTitle: string) => void
  emptyPlaceholderMessage?: string
  initialSelectedByKeys?: Map<string, T>
  rowReorderingMode?: RowReorderingMode
  rowReorderingDisabledMessage?: string
  onRowReorder?: (rows: T[]) => void
  persistColumnWidthsInUrl?: boolean
  initialColumnWidths?: ColumnWidths
  hideColumnBorders?: boolean
  isHeaderSticky?: boolean
  enableKeyboardNavigation?: boolean
} & (
  | { rowSelectionType?: undefined; canSelectAllRows?: boolean }
  | {
      rowSelectionKind: "single-select"
      canSelectAllRows?: false
    }
  | {
      rowSelectionKind: "multi-select"
      canSelectAllRows?: boolean
    }
)

export interface UseTableRes<T> {
  tableProps: TableProps
  footerBarProps: DistributiveOmit<TableFooterBarProps, "actions">
  searchInputProps: ListSearchInputProps
  selected: T[]
  isAllSelected: boolean
  select: (dataToSelect: T[]) => void
  toggleSelected: (rowData: T) => void
  clearSelected: () => void
  columnWidths: ColumnWidths
}

export type ColumnWidths = Record<string, number>

export interface UseTableBodyProps<T>
  extends Pick<
    UseTableProps<T>,
    | "data"
    | "columns"
    | "extractRowKey"
    | "rowSelectionKind"
    | "onClickRow"
    | "extractRowHref"
    | "enableRowHighlighting"
    | "hiddenColumns"
    | "emptyPlaceholderMessage"
    | "rowReorderingMode"
    | "rowReorderingDisabledMessage"
    | "hideColumnBorders"
  > {
  columnWidths: ColumnWidths
  state: TableState<T>
  dispatch: Dispatch<TableAction<T>>
}

export interface UseTableHeaderProps<T>
  extends Pick<
    UseTableProps<T>,
    | "data"
    | "columns"
    | "rowSelectionKind"
    | "canSelectAllRows"
    | "hiddenColumns"
    | "toggleColumnHideShow"
    | "enableColumnReordering"
    | "rowReorderingMode"
    | "hideColumnBorders"
    | "isHeaderSticky"
  > {
  columnWidths: ColumnWidths
  startColumnResize?: StartColumnResizeCallback
  reorderColumn?: ColumnReorderFn
  state: TableState<T>
  dispatch: Dispatch<TableAction<T>>
}

export interface UseTableRowSelectionProps<T>
  extends Pick<UseTableProps<T>, "data" | "extractRowKey"> {
  state: TableState<T>
  dispatch: Dispatch<TableAction<T>>
}

export interface UseTableRowSelectionRes<T>
  extends Pick<
    UseTableRes<T>,
    "selected" | "isAllSelected" | "select" | "toggleSelected"
  > {
  toggleAllSelected: () => void
  clearSelected: () => void
}

export type UseRowOrderProps<T> = Pick<
  UseTableProps<T>,
  "data" | "extractRowKey" | "onRowReorder"
>

export interface TableState<T> {
  expandedGroups: Set<string>
  selectedByKey: Map<string, T>
  lastClickedRowKey: string
  isAllSelected: boolean
}

export type TableAction<T> =
  | {
      type: "toggle_group_expanded"
      groupTitle: string
    }
  | {
      type: "toggle_row_selected"
      rowData: T
      visibleRowData: T[]
    }
  | {
      type: "toggle_row_selected_with_shift"
      rowData: T
      visibleRowData: T[]
    }
  | {
      type: "toggle_all_selected"
    }
  | {
      type: "clear_selected"
    }
  | {
      type: "select_rows"
      rowData: T[]
    }
